import { useCountriesStore } from '@checkout/stores';
import { storeToRefs } from 'pinia';

import { useStore } from '~/composables';
import type { Address } from '~/models';
import type { CountryCodeEnum } from '~/models';

export const getFormattedAddressData = (address: Address, options: { isAddressOptionView?: boolean } = {}): string[] => {
  if (!address) return [];

  const { isAddressOptionView = false } = options;
  const countriesStore = useCountriesStore();
  const { getCountryById } = storeToRefs(countriesStore);
  const { isJpStore, isUsCaStore, isZhHkStore } = useStore();

  const getCountryLabel = (countryCode: string): string => {
    if (!countryCode) {
      return '';
    }

    const country = getCountryById.value(countryCode);
    return country?.label || countryCode;
  };

  const {
    firstname = '',
    lastname = '',
    firstnamekana = '',
    lastnamekana = '',
    country_code = '' as CountryCodeEnum,
    street = [],
    postcode = '',
    region = {},
    city = ''
  } = address;

  let fullName = [firstname, lastname].filter(Boolean).join(' ');

  if (isJpStore.value) {
    fullName = [lastname, firstname].filter(Boolean).join(' ');
  }

  const kanaName = [lastnamekana, firstnamekana].filter(Boolean).join(' ');
  const countryName = getCountryLabel(country_code || '') || country_code || '';
  const streetAddress = isAddressOptionView ? street.filter(Boolean).join(', ') : street[0];

  const formatLocation = (parts: string[]): string => parts.filter(Boolean).join(' ');

  const formatJP = (): string[] => {
    if (isAddressOptionView) {
      return [fullName, kanaName, postcode, region.region, city, streetAddress].filter(Boolean);
    }
    const locationParts = formatLocation([postcode, region.region, city, streetAddress]);

    return [fullName, kanaName, locationParts].filter(Boolean);
  };

  const formatUSCA = (): string[] => {
    if (isAddressOptionView) {
      const postcodeCity = [postcode, city].filter(Boolean).join(' ');

      return [countryName, fullName, streetAddress, postcodeCity, region.region].filter(Boolean);
    }
    const locationParts = formatLocation([streetAddress, postcode, city, region.region]);

    return [countryName, fullName, locationParts].filter(Boolean);
  };

  const formatZHHK = (): string[] => {
    if (isAddressOptionView) {
      return [fullName, streetAddress, city, countryName].filter(Boolean);
    }
    const locationParts = formatLocation([streetAddress, city]);

    return [fullName, locationParts, countryName].filter(Boolean);
  };

  const formatDefault = (): string[] => {
    if (isAddressOptionView) {
      const postcodeCity = [postcode, city].filter(Boolean).join(', ');

      return [fullName, streetAddress, postcodeCity, countryName].filter(Boolean);
    }
    const locationParts = formatLocation([streetAddress, postcode, city]);

    return [fullName, locationParts, countryName].filter(Boolean);
  };

  const regionFormatters = new Map([
    [isJpStore.value, formatJP],
    [isUsCaStore.value, formatUSCA],
    [isZhHkStore.value, formatZHHK]
  ]);
  const formatAddress = regionFormatters.get(true) || formatDefault;

  return formatAddress().filter(Boolean);
};
